import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { DistrictMode, ScoreDimensionMaster } from '@citizen-walk/shared-types';
import { useState } from 'react';
import { archiveMaster, upsertMaster, useScoreDimensionMasters } from './useMasters';
import { MasterHistoryView } from './MasterHistoryView';
import { MasterImportExportButtons, RowExportButton } from './MasterImportExportButtons';
import { stripSystemFields } from './jsonImportExport';
import { ColorSwatch } from './components/ColorSwatch';
import { ModeSelectField } from './components/ModeSelectField';

/**
 * M6 scoreDimensionMasters 管理画面 (docs/04 §3-1 / §3-3 M6、Phase 3.5 admin 第 7 弾)。
 *
 * 旧 `ECW_SCORE_DIMENSIONS` ハードコードを動的化 (docs/04 §3-3 完全準拠、docs/04 L116 「新規」)。
 * 各 dimension は { key, label, description, color, valueRange: [number,number], displayOrder }
 * の 6 フィールド struct (shared-types ScoreDimensionMaster.dimensions)。
 *
 * key は PostDoc.customFields のキーと一致する識別子 (e.g. 'ConvenienceScore')。
 * valueRange は旧仕様で 0..5 想定 (UI では min/max 自由入力、validation で min < max を強制)。
 *
 * Phase 5 で再検討 (R-29):
 *   - 空 dimensions[] master の警告 (enabled: true で保存可能)
 *   - displayOrder 自動採番 / drag-and-drop reorder
 *   - PostDoc.customFields との整合 validation (key の参照整合性)
 */

const COLLECTION_NAME = 'scoreDimensionMasters';

type Dimension = ScoreDimensionMaster['dimensions'][number];

interface FormDraft {
  id: string;
  displayName: string;
  mode: DistrictMode;
  dimensions: Dimension[];
}

const EMPTY_DRAFT: FormDraft = {
  id: '',
  displayName: '',
  mode: 'ecw',
  dimensions: [],
};

const EMPTY_DIMENSION: Dimension = {
  key: '',
  label: '',
  description: '',
  color: '#888888',
  valueRange: [0, 5],
  displayOrder: 0,
};

const INPUT_CLASS =
  'w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400';

function validateScoreDimensionMaster(
  raw: unknown,
): (ScoreDimensionMaster & { id: string }) | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id.trim()) return null;
  if (typeof r.displayName !== 'string') return null;
  if (typeof r.mode !== 'string') return null;
  if (!Array.isArray(r.dimensions)) return null;
  return r as unknown as ScoreDimensionMaster & { id: string };
}

export function ScoreDimensionMastersPage() {
  const state = useScoreDimensionMasters();
  const { toast } = useToast();
  const [editing, setEditing] = useState<FormDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; name: string } | null>(null);

  async function handleImport(importedItems: (ScoreDimensionMaster & { id: string })[]) {
    for (const it of importedItems) {
      const existing =
        state.status === 'ready' ? state.items.find((x) => x.id === it.id) : undefined;
      // Sprint BG-MasterIO follow-up — system field を除去
      const { id, ...rest } = stripSystemFields(it);
      await upsertMaster<ScoreDimensionMaster>({
        collectionName: COLLECTION_NAME,
        id: id as string,
        uid: 'admin-stub',
        isNew: !existing,
        data: rest as Omit<
          ScoreDimensionMaster,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
    }
  }

  function handleNew() {
    setEditing({ ...EMPTY_DRAFT, dimensions: [] });
    setIsNew(true);
  }

  function handleEdit(item: ScoreDimensionMaster & { id: string }) {
    setEditing({
      id: item.id,
      displayName: item.displayName,
      mode: item.mode,
      dimensions: item.dimensions.map((d) => ({
        ...d,
        valueRange: [d.valueRange[0], d.valueRange[1]],
      })),
    });
    setIsNew(false);
  }

  function addDimension() {
    if (!editing) return;
    // displayOrder は既存最大 + 1 で自動採番 (手動編集も可)
    const nextOrder =
      editing.dimensions.length === 0
        ? 0
        : Math.max(...editing.dimensions.map((d) => d.displayOrder)) + 1;
    setEditing({
      ...editing,
      dimensions: [...editing.dimensions, { ...EMPTY_DIMENSION, displayOrder: nextOrder }],
    });
  }

  function updateDimension(index: number, patch: Partial<Dimension>) {
    if (!editing) return;
    const target = editing.dimensions[index];
    if (!target) return;
    const next = [...editing.dimensions];
    next[index] = { ...target, ...patch };
    setEditing({ ...editing, dimensions: next });
  }

  function updateValueRange(index: number, which: 0 | 1, value: number) {
    if (!editing) return;
    const target = editing.dimensions[index];
    if (!target) return;
    const newRange: [number, number] = [target.valueRange[0], target.valueRange[1]];
    newRange[which] = value;
    const next = [...editing.dimensions];
    next[index] = { ...target, valueRange: newRange };
    setEditing({ ...editing, dimensions: next });
  }

  function removeDimension(index: number) {
    if (!editing) return;
    setEditing({
      ...editing,
      dimensions: editing.dimensions.filter((_, i) => i !== index),
    });
  }

  async function handleSave() {
    if (!editing) return;
    if (!editing.displayName.trim()) {
      toast('error', 'displayName は必須です');
      return;
    }
    if (isNew && !editing.id.trim()) {
      toast('error', '新規時は id (kebab-case) を指定してください');
      return;
    }
    // dimensions[] バリデーション
    const cleaned: Dimension[] = [];
    const seenKeys = new Set<string>();
    let dIndex = 0;
    for (const d of editing.dimensions) {
      dIndex++;
      const key = d.key.trim();
      const label = d.label.trim();
      if (!key || !label) {
        toast('error', `dimension ${dIndex}: key と label は必須です`);
        return;
      }
      if (seenKeys.has(key)) {
        toast('error', `dimension ${dIndex}: key "${key}" が重複しています`);
        return;
      }
      seenKeys.add(key);
      const [min, max] = d.valueRange;
      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        toast('error', `dimension ${dIndex}: valueRange の数値が不正です`);
        return;
      }
      if (min >= max) {
        toast(
          'error',
          `dimension ${dIndex}: valueRange は min < max が必要 (現在 ${min} >= ${max})`,
        );
        return;
      }
      if (!Number.isFinite(d.displayOrder)) {
        toast('error', `dimension ${dIndex}: displayOrder の数値が不正です`);
        return;
      }
      cleaned.push({
        key,
        label,
        description: d.description,
        color: d.color.trim() || '#888888',
        valueRange: [min, max],
        displayOrder: d.displayOrder,
      });
    }
    // displayOrder で昇順ソートして保存 (運用時に一覧描画が予測可能)
    cleaned.sort((a, b) => a.displayOrder - b.displayOrder);
    setSaving(true);
    try {
      await upsertMaster<ScoreDimensionMaster>({
        collectionName: COLLECTION_NAME,
        id: editing.id || undefined,
        uid: 'admin-stub', // Phase 5 で実 master role uid に置換
        isNew,
        data: {
          mode: editing.mode,
          displayName: editing.displayName.trim(),
          dimensions: cleaned,
          enabled: true,
          version: 1,
          schemaVersion: 1,
          archivedAt: null,
        },
      });
      toast('success', isNew ? '新規作成しました' : '更新しました');
      setEditing(null);
      setIsNew(false);
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(id: string, name: string) {
    if (!confirm(`「${name}」をアーカイブしますか? (物理削除ではなく archivedAt 設定)`)) return;
    try {
      await archiveMaster(COLLECTION_NAME, id, 'admin-stub'); // Phase 5 で実 master role uid に置換
      toast('success', 'アーカイブしました');
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">スコア軸定義マスター</h1>
        <p className="text-sm text-slate-500 mt-1">
          評価スコア軸 (内部 ID: scoreDimensionMasters、docs/04 §3-1 / §3-3 M6)。旧
          ECW_SCORE_DIMENSIONS ハードコードを動的化。
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Button variant="primary" size="md" onClick={handleNew} disabled={editing !== null}>
          ＋ 新規作成
        </Button>
        <MasterImportExportButtons<ScoreDimensionMaster & { id: string }>
          collectionName={COLLECTION_NAME}
          items={state.status === 'ready' ? state.items : []}
          validator={validateScoreDimensionMaster}
          onImport={handleImport}
          disabled={editing !== null}
          expectedMode={['ecw', 'bousai', 'walker', 'kids-bousai']}
        />
      </div>

      {state.status === 'loading' && <p className="text-slate-500">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-danger-600">読込エラー: {state.error.message}</p>
      )}
      {state.status === 'ready' && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            件数: {state.items.length} (アーカイブ済除外)
          </p>
          <ul className="grid grid-cols-1 gap-2 mb-6">
            {state.items.map((item) => (
              <li key={item.id}>
                <Card padding="md" className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <strong className="text-slate-900">{item.displayName}</strong>
                    <span className="ml-2 text-xs text-slate-500">id: {item.id}</span>
                    <span className="ml-2 text-xs text-slate-500">mode: {item.mode}</span>
                    <span className="ml-2 text-xs text-slate-500">
                      dimensions: {item.dimensions.length}
                    </span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <RowExportButton collectionName={COLLECTION_NAME} item={item} />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEdit(item)}
                      aria-label={`${item.displayName} を編集`}
                    >
                      編集
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setHistoryTarget({ id: item.id, name: item.displayName })}
                      aria-label={`${item.displayName} の履歴を表示`}
                    >
                      履歴
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => void handleArchive(item.id, item.displayName)}
                      aria-label={`${item.displayName} をアーカイブ`}
                    >
                      アーカイブ
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}

      {historyTarget && (
        <MasterHistoryView
          collectionName={COLLECTION_NAME}
          masterId={historyTarget.id}
          masterDisplayName={historyTarget.name}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {editing && (
        <Card padding="lg" className="border-primary-300">
          <h2 className="text-lg font-bold mb-3">{isNew ? '新規作成' : '編集'}</h2>
          <div className="grid grid-cols-1 gap-3">
            {isNew && (
              <div>
                <label className="block text-xs text-slate-600 mb-1" htmlFor="m6-id">
                  ドキュメント ID (id) — kebab-case、保存後変更不可{' '}
                  <span className="text-danger-600">*</span>
                </label>
                <input
                  id="m6-id"
                  type="text"
                  value={editing.id}
                  onChange={(e) => setEditing({ ...editing, id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="例: ecw-default-scores"
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m6-name">
                表示名称 (displayName) <span className="text-danger-600">*</span>
              </label>
              <input
                id="m6-name"
                type="text"
                value={editing.displayName}
                onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m6-mode">
                モード (mode)
              </label>
              {/* MD-3 モードドリブン化: 選択肢は ModeProfile (M10) 登記簿から動的生成 (未登録の現在値も表示)。 */}
              <ModeSelectField
                id="m6-mode"
                value={editing.mode}
                onChange={(mode) => setEditing({ ...editing, mode })}
              />
            </div>

            {/* dimensions[] 編集 */}
            <div className="border-t border-slate-200 pt-3 mt-2">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-bold text-slate-700">
                  スコア軸 (dimensions[]、評価次元、{editing.dimensions.length} 件)
                </label>
                <Button variant="secondary" size="sm" onClick={addDimension}>
                  ＋ dimension 追加
                </Button>
              </div>
              {editing.dimensions.length === 0 ? (
                <p className="text-xs text-slate-500">
                  dimension がありません。「＋ dimension 追加」で追加。
                </p>
              ) : (
                <ul className="grid grid-cols-1 gap-3">
                  {editing.dimensions.map((dim, i) => (
                    <li
                      key={dim.key.trim() || `__draft-${i}`}
                      className="border border-slate-200 rounded-md p-3 bg-slate-50"
                    >
                      <div className="grid grid-cols-1 gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-slate-500 shrink-0">#{i + 1}</span>
                          <input
                            type="text"
                            value={dim.key}
                            onChange={(e) => updateDimension(i, { key: e.target.value })}
                            className={`${INPUT_CLASS} flex-1 min-w-32`}
                            placeholder="key (例: ConvenienceScore)"
                            aria-label={`スコア軸 ${i + 1} スコアキー (key)`}
                          />
                          <input
                            type="text"
                            value={dim.label}
                            onChange={(e) => updateDimension(i, { label: e.target.value })}
                            className={`${INPUT_CLASS} flex-1 min-w-32`}
                            placeholder="label (例: 快適性)"
                            aria-label={`スコア軸 ${i + 1} 表示名 (label)`}
                          />
                          <span className="shrink-0">
                            <ColorSwatch
                              value={dim.color}
                              onChange={(c) => updateDimension(i, { color: c ?? dim.color })}
                              showClear={false}
                              size={24}
                              ariaLabel={`スコア軸 ${i + 1} 色 (color)`}
                            />
                          </span>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeDimension(i)}
                            aria-label={`スコア軸 ${i + 1} 削除`}
                          >
                            ✕
                          </Button>
                        </div>
                        <textarea
                          value={dim.description}
                          onChange={(e) => updateDimension(i, { description: e.target.value })}
                          rows={2}
                          className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400 resize-y"
                          placeholder="description (この次元の説明文)"
                          aria-label={`スコア軸 ${i + 1} 説明 (description)`}
                        />
                        <div className="flex items-center gap-2 flex-wrap text-xs">
                          <label className="flex items-center gap-1 text-slate-700">
                            valueRange:
                            <input
                              type="number"
                              value={dim.valueRange[0]}
                              onChange={(e) => updateValueRange(i, 0, Number(e.target.value))}
                              className="w-16 px-2 py-1 border border-slate-300 rounded"
                              aria-label={`スコア軸 ${i + 1} 値範囲 最小 (valueRange min)`}
                            />
                            〜
                            <input
                              type="number"
                              value={dim.valueRange[1]}
                              onChange={(e) => updateValueRange(i, 1, Number(e.target.value))}
                              className="w-16 px-2 py-1 border border-slate-300 rounded"
                              aria-label={`スコア軸 ${i + 1} 値範囲 最大 (valueRange max)`}
                            />
                          </label>
                          <label className="flex items-center gap-1 text-slate-700 ml-3">
                            displayOrder:
                            <input
                              type="number"
                              value={dim.displayOrder}
                              onChange={(e) =>
                                updateDimension(i, { displayOrder: Number(e.target.value) })
                              }
                              className="w-16 px-2 py-1 border border-slate-300 rounded"
                              aria-label={`スコア軸 ${i + 1} 表示順 (displayOrder)`}
                            />
                          </label>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-slate-500 mt-2">
                保存時に displayOrder 昇順でソートされます (UI 描画順の一意化)。
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-2 border-t border-slate-200 pt-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setEditing(null);
                  setIsNew(false);
                }}
                disabled={saving}
              >
                キャンセル
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
